import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SKU } from 'src/redux/billing/billing.types';
import { Check, Lightbulb } from 'lucide-react';
import { fuzzyFilterSimilarity } from 'src/services/fuzzy-match';
import { formatSkuLabel } from 'src/components/sku-helper/sku-utils';

interface BorderedSkuSelectProps {
  skus: Array<SKU>;
  value: string; // skuId
  onChange: (skuId: string) => void;
  label?: string;
  error?: string;
  className?: string;
  textClassName?: string;
  // Suggestion props
  campaignTitle?: string; // Used for fuzzy matching and DNB detection
  attentionNeeded?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
}

const BorderedSkuSelect: React.FC<BorderedSkuSelectProps> = ({
  skus,
  value,
  onChange,
  label,
  error,
  className,
  textClassName,
  campaignTitle,
  attentionNeeded = false,
  isLoading = false,
  disabled = false,
}) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredSkus, setFilteredSkus] = useState<SKU[]>(skus);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Calculate dropdown position for portal
  useEffect(() => {
    if (showDropdown && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Smart suggestion logic
  const suggestedSkus = useMemo(() => {
    if (skus.length === 0) return [];
    const suggestions: SKU[] = [];

    // Check for DNB SKU if campaign title contains "DNB" exactly
    if (campaignTitle && campaignTitle.includes('DNB')) {
      const dnbSku = skus.find(sku => sku.sku === 'DNB');
      if (dnbSku && !suggestions.find(s => s.id === dnbSku.id)) {
        suggestions.push(dnbSku);
      }
    }

    // Fuzzy matching based on campaign title
    if (campaignTitle) {
      const results = fuzzyFilterSimilarity(
        skus,
        campaignTitle,
        (sku: SKU) => formatSkuLabel(sku),
        { limit: 3, threshold: 15 }
      );
      const fuzzyMatch = results.map((result) => result.item).slice(0, 1)[0];
      if (fuzzyMatch && !suggestions.find(s => s.id === fuzzyMatch.id)) {
        suggestions.push(fuzzyMatch);
      }
    }

    return suggestions;
  }, [skus, campaignTitle]);

  const handleSkuSelect = (selectedSkuId: string) => {
    onChange(selectedSkuId);
    setShowDropdown(false);
  };

  const handleDropdownToggle = () => {
    if (!disabled && !isLoading) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
    setFilteredSkus(
      skus.filter((sku) =>
        formatSkuLabel(sku).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };

  const selectedSku = skus.find(s => s.id === value);
  const displayText = selectedSku ? formatSkuLabel(selectedSku) : 'Unassigned';

  // Sort SKUs alphabetically and filter out suggested SKUs
  const sortedSkus = useMemo(() => {
    const suggestedIds = new Set(suggestedSkus.map(s => s.id));
    const filtered = filteredSkus.filter(sku => !suggestedIds.has(sku.id));
    return filtered.sort((a, b) => {
      const nameA = formatSkuLabel(a);
      const nameB = formatSkuLabel(b);
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
    });
  }, [filteredSkus, suggestedSkus]);

  const dropdownContent = showDropdown && dropdownPosition && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${Math.max(dropdownPosition.width, 320)}px`, // Minimum 320px width for more substantial feel
        minWidth: '320px',
      }}
    >
      <div className="p-1">
        <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
          <input
            type="text"
            placeholder="Search SKUs"
            className="w-full text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
        </div>
        {/* Verify option */}
        {attentionNeeded && value && selectedSku && (
          <>
            <div className="px-3 text-xs text-gray-500">
              Verify
            </div>
            <button
              onClick={() => handleSkuSelect(value)}
              className="group w-full text-left px-3 py-2 hover:bg-green-50 rounded text-sm transition-colors duration-150 flex items-center gap-2 bg-green-50/50 border-b border-gray-200 hover:bg-green-501"
            >
              <Check className="size-3 flex-shrink-0 text-green-600 group-hover:text-white transition-colors duration-150" />
              <span className="truncate font-medium text-gray-800 group-hover:text-white transition-colors duration-150">
                {formatSkuLabel(selectedSku)}
              </span>
            </button>
          </>
        )}
        {/* Suggested SKUs at the top */}
        {!value && suggestedSkus && suggestedSkus.length > 0 && (
          <>
            <div className="px-3 text-xs text-gray-500">
              Suggestions
            </div>
            {suggestedSkus.map((sku) => (
              <button
                key={sku.id}
                onClick={() => handleSkuSelect(sku.id)}
                className="group w-full text-left px-3 py-2 hover:bg-yellow-50 rounded text-sm transition-colors duration-150 flex items-center gap-2 bg-yellow-50/50 border-b border-yellow-100 hover:bg-yellow-500"
              >
                <Lightbulb className="size-3 flex-shrink-0 text-yellow-600 group-hover:text-white transition-colors duration-150" />
                <span className="truncate font-medium text-gray-800 group-hover:text-white transition-colors duration-150">
                  {formatSkuLabel(sku)}
                </span>
              </button>
            ))}
          </>
        )}
        {/* Regular SKUs list */}
        {sortedSkus.length === 0 && (!suggestedSkus || suggestedSkus.length === 0) ? (
          <div className="px-3 py-2 text-sm text-gray-500 text-center">
            No SKUs available
          </div>
        ) : (
          sortedSkus.map((sku) => (
            <button
              key={sku.id}
              onClick={() => handleSkuSelect(sku.id)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 flex items-center gap-2 ${
                value === sku.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              {/* <Receipt className="size-3 flex-shrink-0" /> */}
              <span className="truncate">
                {formatSkuLabel(sku)}
              </span>
              {value === sku.id && (
                <div className="ml-auto">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                </div>
              )}
            </button>
          ))
        )}
        {value && (
          <>
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={() => handleSkuSelect('')}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 text-gray-500 flex items-center gap-2"
              >
                <div className="size-3 flex-shrink-0 border border-gray-300 rounded" />
                <span>
                  Unassign SKU
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col">
        <div
          className={`group relative rounded cursor-pointer ${error ? 'border-red-500' : ''} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${className || ''}`}
          style={{
            paddingLeft: '1px',
            paddingRight: '5px',
            display: 'inline-block',
            maxWidth: '250px',
            maxHeight: 36,
          }}
        >
          {label && (
            <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500 z-10 pointer-events-none">
              {label}
            </label>
          )}
          <div
            ref={triggerRef}
            onClick={handleDropdownToggle}
            className={`
              appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
              border-1 border-transparent 
              group-hover:border-gray-500 
              group-hover:border 
              focus:outline-none
              flex items-center justify-between
              ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500 border border-red-500' : ''}
              ${disabled || isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
            style={{
              maxHeight: 36,
            }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* <Receipt
                className={`size-4 flex-shrink-0 ${attentionNeeded ? 'text-red-500' : 'text-gray-500'}`}
              /> */}
              <span
                className={`text-sm truncate ${textClassName || 'text-gray-500'} ${attentionNeeded ? 'text-red-500 font-bold' : ''}`}
                title={displayText}
              >
                {isLoading ? 'Updating...' : displayText}
              </span>
            </div>
            {/* <ChevronDown className={`size-4 flex-shrink-0 text-gray-500 ${disabled || isLoading ? '' : 'cursor-pointer'}`} /> */}
          </div>
        </div>
        {error && (
          <span className="mt-1 text-xs text-red-500">
            {error}
          </span>
        )}
      </div>
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default BorderedSkuSelect;

